import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Building2, Percent, FileImage, Mail, Plus, Edit2, Trash2, Upload, Info, Tags
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DataImport from "@/components/DataImport";
import SupplierCategoriesSettings from "@/components/SupplierCategoriesSettings";
import { apiFetch, API } from "@/utils/api";
import {
  getCompanyProfile, updateCompanyProfile, invalidateCompanyProfile
} from "@/utils/companyProfile";

// Definido fuera del componente: si se define dentro, React lo remonta en cada
// render y los inputs pierden el foco al teclear.
function Field({ label, children, help }) {
  return (
    <div>
      <label className="text-sm font-medium mb-1 block text-slate-700">{label}</label>
      {children}
      {help && <p className="text-xs text-slate-400 mt-1">{help}</p>}
    </div>
  );
}

const EMPTY_IDENTIDAD = {
  tipo_persona: "fisica", name: "", nombre_comercial: "", nif: "", address: "",
  city: "", provincia: "", postal_code: "", country: "", email: "", phone: "", iban: "",
  swift: "", epigrafe_iae: "", actividad_descripcion: "",
};

export default function Settings() {
  const [loading, setLoading] = useState(true);

  // ---- Emisor ----
  const [identidad, setIdentidad] = useState(EMPTY_IDENTIDAD);
  const [savingEmisor, setSavingEmisor] = useState(false);

  // ---- Fiscalidad ----
  const [fiscalFacturas, setFiscalFacturas] = useState({
    iva_default: "21", irpf_default: "0", aplicar_irpf_default: false, regimen_iva: "general",
  });
  const [fiscalAutonomo, setFiscalAutonomo] = useState({
    cuota_autonomo_mensual: "", regimen_cotizacion: "", regimen_irpf: "no_aplica",
  });
  // ---- IA (A5/§7: consentimiento explícito por tenant, off por defecto) ----
  const [iaConsent, setIaConsent] = useState(false);
  const [savingIa, setSavingIa] = useState(false);

  const [facturacion, setFacturacion] = useState({
    serie_normal: "", serie_rectificativas: "",
  });
  const [savingFiscal, setSavingFiscal] = useState(false);

  // ---- Documento ----
  const [documento, setDocumento] = useState({
    logo_path: null, color_acento: "", texto_proteccion_datos: "", texto_legal_pie: "",
    decimales: "2", formato_numerico: "es",
  });
  const [savingDocumento, setSavingDocumento] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreviewError, setLogoPreviewError] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0); // rompe la caché del <img> tras subir
  const [hasProfile, setHasProfile] = useState(true);
  const logoInputRef = useRef(null);

  // ---- Alertas (destinatarios email) ----
  const [recipients, setRecipients] = useState([]);
  const [showRecipientForm, setShowRecipientForm] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [recipientForm, setRecipientForm] = useState({ name: "", email: "", is_active: true });

  const loadProfile = useCallback(async ({ force = false } = {}) => {
    try {
      const p = await getCompanyProfile({ force });
      const id = p?.identidad ?? {};
      setIdentidad({
        tipo_persona: id.tipo_persona ?? "fisica",
        name: id.name ?? "",
        nombre_comercial: id.nombre_comercial ?? "",
        nif: id.nif ?? "",
        address: id.address ?? "",
        city: id.city ?? "",
        provincia: id.provincia ?? "",
        postal_code: id.postal_code ?? "",
        country: id.country ?? "",
        email: id.email ?? "",
        phone: id.phone ?? "",
        iban: id.iban ?? "",
        swift: id.swift ?? "",
        epigrafe_iae: id.epigrafe_iae ?? "",
        actividad_descripcion: id.actividad_descripcion ?? "",
      });
      const ff = p?.fiscal_facturas ?? {};
      setFiscalFacturas({
        iva_default: String(ff.iva_default ?? 21),
        irpf_default: String(ff.irpf_default ?? 0),
        aplicar_irpf_default: ff.aplicar_irpf_default ?? false,
        regimen_iva: ff.regimen_iva ?? "general",
      });
      const fa = p?.fiscal_autonomo ?? {};
      setFiscalAutonomo({
        cuota_autonomo_mensual: fa.cuota_autonomo_mensual ?? "",
        regimen_cotizacion: fa.regimen_cotizacion ?? "",
        regimen_irpf: fa.regimen_irpf ?? "no_aplica",
      });
      const fac = p?.facturacion ?? {};
      setFacturacion({
        serie_normal: fac.serie_normal ?? "",
        serie_rectificativas: fac.serie_rectificativas ?? "",
      });
      const doc = p?.documento ?? {};
      setDocumento({
        logo_path: doc.logo_path ?? null,
        color_acento: doc.color_acento ?? "",
        texto_proteccion_datos: doc.texto_proteccion_datos ?? "",
        texto_legal_pie: doc.texto_legal_pie ?? "",
        decimales: String(doc.decimales ?? 2),
        formato_numerico: doc.formato_numerico ?? "es",
      });
      setHasProfile(Boolean(p?.has_profile));
      setIaConsent(Boolean(p?.ia?.consentimiento));
      setLogoPreviewError(false);
    } catch (e) {
      toast.error("No se pudo cargar el perfil de empresa");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecipients = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/settings/email-recipients`);
      if (res.ok) setRecipients(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadProfile(); fetchRecipients(); }, [loadProfile, fetchRecipients]);

  // ---- Guardar Emisor ----
  const handleSaveEmisor = async () => {
    setSavingEmisor(true);
    try {
      await updateCompanyProfile({ identidad: { ...identidad } });
      invalidateCompanyProfile();
      await loadProfile({ force: true });
      toast.success("Datos del emisor guardados");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar el emisor");
    } finally {
      setSavingEmisor(false);
    }
  };

  // ---- Guardar IA (A5/§7) ----
  const handleSaveIa = async () => {
    setSavingIa(true);
    try {
      await updateCompanyProfile({ ia: { consentimiento: iaConsent } });
      invalidateCompanyProfile();
      await loadProfile({ force: true });
      toast.success(iaConsent
        ? "Funciones de IA activadas"
        : "Funciones de IA desactivadas");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar");
    } finally {
      setSavingIa(false);
    }
  };

  // ---- Guardar Fiscalidad ----
  const handleSaveFiscalidad = async () => {
    const irpf = parseFloat(fiscalFacturas.irpf_default);
    if (Number.isNaN(irpf) || irpf < 0 || irpf > 47) {
      toast.error("El IRPF por defecto debe estar entre 0 y 47");
      return;
    }
    const cuotaRaw = fiscalAutonomo.cuota_autonomo_mensual;
    const cuota = cuotaRaw === "" || cuotaRaw === null ? null : parseFloat(cuotaRaw);
    if (cuota !== null && (Number.isNaN(cuota) || cuota < 0)) {
      toast.error("La cuota de autónomo debe ser un número positivo");
      return;
    }
    setSavingFiscal(true);
    try {
      await updateCompanyProfile({
        fiscal_facturas: {
          iva_default: Number(fiscalFacturas.iva_default),
          irpf_default: irpf,
          aplicar_irpf_default: fiscalFacturas.aplicar_irpf_default,
          regimen_iva: fiscalFacturas.regimen_iva,
        },
        fiscal_autonomo: {
          cuota_autonomo_mensual: cuota,
          regimen_cotizacion: fiscalAutonomo.regimen_cotizacion || null,
          regimen_irpf: fiscalAutonomo.regimen_irpf,
        },
        facturacion: {
          serie_normal: facturacion.serie_normal,
          serie_rectificativas: facturacion.serie_rectificativas,
        },
      });
      invalidateCompanyProfile();
      await loadProfile({ force: true });
      toast.success("Fiscalidad guardada");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar la fiscalidad");
    } finally {
      setSavingFiscal(false);
    }
  };

  // ---- Documento ----
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error("El logo debe ser PNG o JPG");
      e.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("El logo no puede superar los 2 MB");
      e.target.value = "";
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch(`${API}/settings/company-profile/logo`, {
        method: "POST",
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        setDocumento((prev) => ({ ...prev, logo_path: data.logo_path }));
        setLogoPreviewError(false);
        setLogoVersion((v) => v + 1);
        invalidateCompanyProfile();
        toast.success("Logo subido");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo subir el logo");
      }
    } catch (err) {
      toast.error("Error de conexión al subir el logo");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleSaveDocumento = async () => {
    const hex = documento.color_acento?.trim() || null;
    if (hex && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) {
      toast.error("El color de acento debe ser un código hex válido (p. ej. #1e293b)");
      return;
    }
    setSavingDocumento(true);
    try {
      await updateCompanyProfile({
        documento: {
          color_acento: hex,
          texto_proteccion_datos: documento.texto_proteccion_datos?.trim() || null,
          texto_legal_pie: documento.texto_legal_pie,
          decimales: Number(documento.decimales),
          formato_numerico: documento.formato_numerico,
        },
      });
      invalidateCompanyProfile();
      await loadProfile({ force: true });
      toast.success("Ajustes de documento guardados");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar el documento");
    } finally {
      setSavingDocumento(false);
    }
  };

  // Logo EFECTIVO (el subido, o el default del tenant legacy): lo sirve el
  // backend autenticado; 404 = tenant de producto sin logo (facturas sin logo).
  const logoUrl = `${API}/settings/company-profile/logo?v=${logoVersion}`;
  const usingDefaultLogo = !documento.logo_path && !hasProfile;

  // ---- Destinatarios ----
  const handleSaveRecipient = async () => {
    const method = editingRecipient ? "PUT" : "POST";
    const url = editingRecipient
      ? `${API}/settings/email-recipients/${editingRecipient.recipient_id}`
      : `${API}/settings/email-recipients`;
    try {
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(recipientForm),
      });
      if (res.ok) {
        toast.success(editingRecipient ? "Destinatario actualizado" : "Destinatario añadido");
        setShowRecipientForm(false);
        setEditingRecipient(null);
        setRecipientForm({ name: "", email: "", is_active: true });
        fetchRecipients();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error");
      }
    } catch (e) { toast.error("Error de conexión"); }
  };

  const handleDeleteRecipient = async (id) => {
    if (!window.confirm("¿Eliminar destinatario?")) return;
    try {
      const res = await apiFetch(`${API}/settings/email-recipients/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Eliminado"); fetchRecipients(); }
    } catch (e) { toast.error("Error"); }
  };

  const handleToggleRecipient = async (recipient) => {
    try {
      await apiFetch(`${API}/settings/email-recipients/${recipient.recipient_id}`, {
        method: "PUT",
        body: JSON.stringify({ name: recipient.name, email: recipient.email, is_active: !recipient.is_active }),
      });
      fetchRecipients();
    } catch (e) { toast.error("Error"); }
  };

  const esFisica = identidad.tipo_persona === "fisica";

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-page">
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Ajustes</h1>
        <div className="text-center py-12 text-slate-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight" data-testid="settings-title">Ajustes</h1>
        <p className="text-sm text-slate-500 mt-1">Perfil de empresa, fiscalidad, documento y alertas</p>
      </div>

      <Tabs defaultValue="emisor">
        <TabsList className="bg-slate-100 h-auto flex-wrap justify-start">
          <TabsTrigger value="emisor" data-testid="settings-tab-emisor">
            <Building2 className="w-4 h-4 mr-2" /> Emisor
          </TabsTrigger>
          <TabsTrigger value="fiscalidad" data-testid="settings-tab-fiscalidad">
            <Percent className="w-4 h-4 mr-2" /> Fiscalidad
          </TabsTrigger>
          <TabsTrigger value="documento" data-testid="settings-tab-documento">
            <FileImage className="w-4 h-4 mr-2" /> Documento
          </TabsTrigger>
          <TabsTrigger value="categorias" data-testid="settings-tab-categorias">
            <Tags className="w-4 h-4 mr-2" /> Categorías de proveedor
          </TabsTrigger>
          <TabsTrigger value="ia" data-testid="settings-tab-ia">
            IA
          </TabsTrigger>
          <TabsTrigger value="alertas" data-testid="settings-tab-alertas">
            <Mail className="w-4 h-4 mr-2" /> Alertas
          </TabsTrigger>
          <TabsTrigger value="importar" data-testid="settings-tab-importar">
            <Upload className="w-4 h-4 mr-2" /> Importar datos
          </TabsTrigger>
        </TabsList>

        {/* ===== EMISOR ===== */}
        <TabsContent value="emisor" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5 max-w-3xl">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Identidad del emisor</h3>
              <p className="text-sm text-slate-500">Datos que aparecen en tus facturas emitidas</p>
            </div>

            <Field label="Tipo de persona">
              <Select
                value={identidad.tipo_persona}
                onValueChange={(v) => setIdentidad((prev) => ({ ...prev, tipo_persona: v }))}
              >
                <SelectTrigger className="max-w-xs" data-testid="settings-tipo-persona-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fisica">Persona física (autónomo)</SelectItem>
                  <SelectItem value="juridica">Sociedad</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={esFisica ? "Nombre y apellidos" : "Razón social"}>
                <Input
                  data-testid="settings-name-input"
                  value={identidad.name}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={esFisica ? "Nombre y apellidos" : "Razón social"}
                />
              </Field>
              <Field label="Nombre comercial">
                <Input
                  data-testid="settings-nombre-comercial-input"
                  value={identidad.nombre_comercial}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, nombre_comercial: e.target.value }))}
                  placeholder="Nombre comercial (opcional)"
                />
              </Field>
              <Field label="NIF">
                <Input
                  data-testid="settings-nif-input"
                  value={identidad.nif}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, nif: e.target.value }))}
                  placeholder="B12345678"
                />
              </Field>
              <Field label="Dirección">
                <Input
                  data-testid="settings-address-input"
                  value={identidad.address}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, address: e.target.value }))}
                  placeholder="Calle, número, piso"
                />
              </Field>
              <Field label="Ciudad">
                <Input
                  data-testid="settings-city-input"
                  value={identidad.city}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, city: e.target.value }))}
                />
              </Field>
              <Field label="Provincia">
                <Input
                  data-testid="settings-provincia-input"
                  value={identidad.provincia}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, provincia: e.target.value }))}
                />
              </Field>
              <Field label="Código postal">
                <Input
                  data-testid="settings-postal-code-input"
                  value={identidad.postal_code}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, postal_code: e.target.value }))}
                />
              </Field>
              <Field label="País">
                <Input
                  data-testid="settings-country-input"
                  value={identidad.country}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, country: e.target.value }))}
                  placeholder="España"
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  data-testid="settings-email-input"
                  value={identidad.email}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, email: e.target.value }))}
                />
              </Field>
              <Field label="Teléfono">
                <Input
                  data-testid="settings-phone-input"
                  value={identidad.phone}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </Field>
              <Field label="IBAN">
                <Input
                  data-testid="settings-iban-input"
                  value={identidad.iban}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, iban: e.target.value }))}
                  placeholder="ES00 0000 0000 0000 0000 0000"
                />
              </Field>
              <Field label="BIC/SWIFT">
                <Input
                  data-testid="settings-swift-input"
                  value={identidad.swift}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, swift: e.target.value }))}
                />
              </Field>
              <Field label="Epígrafe IAE">
                <Input
                  data-testid="settings-epigrafe-iae-input"
                  value={identidad.epigrafe_iae}
                  onChange={(e) => setIdentidad((prev) => ({ ...prev, epigrafe_iae: e.target.value }))}
                  placeholder="p. ej. 842"
                />
              </Field>
            </div>

            <Field label="Descripción de la actividad">
              <Textarea
                data-testid="settings-actividad-input"
                rows={2}
                value={identidad.actividad_descripcion}
                onChange={(e) => setIdentidad((prev) => ({ ...prev, actividad_descripcion: e.target.value }))}
                placeholder="p. ej. asesoría fiscal para pymes — se usa para personalizar la IA"
              />
            </Field>

            <div className="flex justify-end">
              <Button
                data-testid="settings-save-emisor"
                onClick={handleSaveEmisor}
                disabled={savingEmisor}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {savingEmisor ? "Guardando..." : "Guardar emisor"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ===== FISCALIDAD ===== */}
        <TabsContent value="fiscalidad" className="mt-4">
          <div className="space-y-4 max-w-3xl">
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Facturas</h3>
                <p className="text-sm text-slate-500">Valores por defecto al crear facturas nuevas</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="IVA por defecto">
                  <Select
                    value={fiscalFacturas.iva_default}
                    onValueChange={(v) => setFiscalFacturas((prev) => ({ ...prev, iva_default: v }))}
                  >
                    <SelectTrigger data-testid="settings-iva-default-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 %</SelectItem>
                      <SelectItem value="4">4 %</SelectItem>
                      <SelectItem value="10">10 %</SelectItem>
                      <SelectItem value="21">21 %</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="IRPF por defecto (%)">
                  <Input
                    type="number"
                    min={0}
                    max={47}
                    step="0.1"
                    data-testid="settings-irpf-default-input"
                    value={fiscalFacturas.irpf_default}
                    onChange={(e) => setFiscalFacturas((prev) => ({ ...prev, irpf_default: e.target.value }))}
                  />
                </Field>
                <Field label="Régimen de IVA">
                  <Select
                    value={fiscalFacturas.regimen_iva}
                    onValueChange={(v) => setFiscalFacturas((prev) => ({ ...prev, regimen_iva: v }))}
                  >
                    <SelectTrigger data-testid="settings-regimen-iva-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="exento">Exento (art. 20)</SelectItem>
                      {/* Recargo de equivalencia y franquicia: ocultos hasta que se
                          implementen (roadmap 1.5) — un régimen elegible que no hace
                          nada invita a facturar mal. Solo se muestran si el perfil ya
                          los tenía guardados, para no dejar el selector en blanco. */}
                      {fiscalFacturas.regimen_iva === "recargo_equivalencia" && (
                        <SelectItem value="recargo_equivalencia">Recargo de equivalencia — sin efecto en esta versión</SelectItem>
                      )}
                      {fiscalFacturas.regimen_iva === "franquicia" && (
                        <SelectItem value="franquicia">Franquicia — sin efecto en esta versión</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  {(fiscalFacturas.regimen_iva === "recargo_equivalencia" ||
                    fiscalFacturas.regimen_iva === "franquicia") && (
                    <div
                      className="flex items-start gap-2 mt-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800"
                      data-testid="settings-aviso-regimen-sin-efecto"
                    >
                      <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                      <p>
                        Este régimen <strong>aún no tiene efecto</strong> en la aplicación:
                        las facturas y los exports NO aplican recargo de equivalencia ni
                        franquicia. Si tu actividad lo necesita, consulta con tu asesoría
                        antes de usar LiquidArc para facturar.
                      </p>
                    </div>
                  )}
                  {fiscalFacturas.regimen_iva === "exento" && (
                    <div
                      className="flex items-start gap-2 mt-2 p-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-500"
                      data-testid="settings-aviso-exento"
                    >
                      <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
                      <p>
                        Actividad exenta (art. 20): las facturas nuevas saldrán con IVA 0 y la
                        mención legal en el PDF. Si además realizas actividad sujeta a IVA
                        (actividad mixta), la prorrata NO se calcula automáticamente —
                        consúltalo con tu gestoría.
                      </p>
                    </div>
                  )}
                </Field>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    checked={fiscalFacturas.aplicar_irpf_default}
                    onCheckedChange={(v) => setFiscalFacturas((prev) => ({ ...prev, aplicar_irpf_default: v }))}
                    data-testid="settings-aplicar-irpf-switch"
                  />
                  <div>
                    <label className="text-sm text-slate-700">Aplicar IRPF por defecto en facturas nuevas</label>
                    <p className="text-xs text-slate-400">
                      {esFisica
                        ? "Habitual en autónomos profesionales: se descuenta en cada factura emitida."
                        : "Las sociedades normalmente no aplican retención en sus facturas."}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5" data-testid="settings-bloque-facturacion">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Facturación</h3>
                <p className="text-sm text-slate-500">Series de numeración de las facturas emitidas</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label="Serie de facturas"
                  help="Se aplicará a las facturas nuevas. Déjalo vacío para usar la serie por defecto."
                >
                  <Input
                    data-testid="settings-serie-normal-input"
                    value={facturacion.serie_normal}
                    onChange={(e) => setFacturacion((prev) => ({ ...prev, serie_normal: e.target.value }))}
                    placeholder={'p. ej. "2026" o "F2026"'}
                    maxLength={20}
                  />
                </Field>
                <Field
                  label="Serie de rectificativas"
                  help="Las facturas rectificativas (importe negativo) usarán esta serie separada — requisito del reglamento de facturación."
                >
                  <Input
                    data-testid="settings-serie-rectificativas-input"
                    value={facturacion.serie_rectificativas}
                    onChange={(e) => setFacturacion((prev) => ({ ...prev, serie_rectificativas: e.target.value }))}
                    placeholder="p. ej. R2026"
                    maxLength={20}
                  />
                </Field>
              </div>
            </div>

            {esFisica && (
              <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5" data-testid="settings-bloque-autonomo">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Autónomo</h3>
                  <p className="text-sm text-slate-500">Datos de cotización y régimen de IRPF</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label="Cuota de autónomo mensual (€)"
                    help="lo que pagas de verdad cada mes, p. ej. 80 € con tarifa plana"
                  >
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      data-testid="settings-cuota-autonomo-input"
                      value={fiscalAutonomo.cuota_autonomo_mensual}
                      onChange={(e) => setFiscalAutonomo((prev) => ({ ...prev, cuota_autonomo_mensual: e.target.value }))}
                      placeholder="p. ej. 80"
                    />
                  </Field>
                  <Field label="Régimen de cotización">
                    <Select
                      value={fiscalAutonomo.regimen_cotizacion || undefined}
                      onValueChange={(v) => setFiscalAutonomo((prev) => ({ ...prev, regimen_cotizacion: v }))}
                    >
                      <SelectTrigger data-testid="settings-regimen-cotizacion-select">
                        <SelectValue placeholder="Selecciona régimen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tarifa_plana">Tarifa plana</SelectItem>
                        <SelectItem value="base_minima">Base mínima</SelectItem>
                        <SelectItem value="otra">Otra</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Régimen IRPF">
                    <Select
                      value={fiscalAutonomo.regimen_irpf}
                      onValueChange={(v) => setFiscalAutonomo((prev) => ({ ...prev, regimen_irpf: v }))}
                    >
                      <SelectTrigger data-testid="settings-regimen-irpf-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="directa_simplificada">Estimación directa simplificada</SelectItem>
                        <SelectItem value="directa_normal">Estimación directa normal</SelectItem>
                        <SelectItem value="no_aplica">No aplica</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                data-testid="settings-save-fiscalidad"
                onClick={handleSaveFiscalidad}
                disabled={savingFiscal}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {savingFiscal ? "Guardando..." : "Guardar fiscalidad"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ===== DOCUMENTO ===== */}
        <TabsContent value="documento" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5 max-w-3xl">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Aspecto del documento</h3>
              <p className="text-sm text-slate-500">Personalización de las facturas en PDF</p>
            </div>

            <Field label="Logo (PNG o JPG, máx. 2 MB)">
              <div className="flex items-center gap-4">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  data-testid="settings-logo-input"
                  onChange={handleLogoUpload}
                />
                <Button
                  variant="outline"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="settings-logo-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingLogo ? "Subiendo..." : "Subir logo"}
                </Button>
                <div className="flex items-center gap-3">
                  {!logoPreviewError ? (
                    <>
                      <img
                        src={logoUrl}
                        alt="Logo de las facturas"
                        className="h-12 max-w-[160px] object-contain border border-slate-200 rounded bg-white p-1"
                        onError={() => setLogoPreviewError(true)}
                      />
                      {usingDefaultLogo && (
                        <span className="text-xs text-slate-400">
                          Logo por defecto (es el que sale hoy en tus facturas)
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-slate-500">
                      Sin logo — las facturas salen sin logo
                    </span>
                  )}
                </div>
              </div>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Color de acento" help="vacío = turquesa por defecto (#4ECBC4), el de tus facturas de hoy">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    className="h-9 w-12 rounded border border-slate-200 cursor-pointer p-0.5"
                    data-testid="settings-color-acento-picker"
                    value={/^#[0-9a-fA-F]{6}$/.test(documento.color_acento || "") ? documento.color_acento : "#4ECBC4"}
                    onChange={(e) => setDocumento((prev) => ({ ...prev, color_acento: e.target.value }))}
                  />
                  <Input
                    className="max-w-[140px]"
                    data-testid="settings-color-acento-input"
                    value={documento.color_acento ?? ""}
                    onChange={(e) => setDocumento((prev) => ({ ...prev, color_acento: e.target.value }))}
                    placeholder="#4ECBC4"
                  />
                </div>
              </Field>
              <Field label="Decimales">
                <Select
                  value={documento.decimales}
                  onValueChange={(v) => setDocumento((prev) => ({ ...prev, decimales: v }))}
                >
                  <SelectTrigger data-testid="settings-decimales-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0</SelectItem>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Formato numérico">
                <Select
                  value={documento.formato_numerico}
                  onValueChange={(v) => setDocumento((prev) => ({ ...prev, formato_numerico: v }))}
                >
                  <SelectTrigger data-testid="settings-formato-numerico-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">1.234,56 (España)</SelectItem>
                    <SelectItem value="en">1,234.56 (anglosajón)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field
              label="Texto de protección de datos para facturas"
              help="sale como bloque «Protección de datos (RGPD)» al pie de cada factura. Vacío = la factura sale sin cláusula. Redáctalo con tu asesor de protección de datos; una línea en blanco separa párrafos"
            >
              <Textarea
                data-testid="settings-texto-proteccion-datos-input"
                rows={5}
                value={documento.texto_proteccion_datos}
                onChange={(e) => setDocumento((prev) => ({ ...prev, texto_proteccion_datos: e.target.value }))}
              />
            </Field>

            <Field
              label="Texto legal adicional de pie"
              help="se añade al pie de la factura, debajo del texto de protección de datos (si lo hay)"
            >
              <Textarea
                data-testid="settings-texto-legal-input"
                rows={3}
                value={documento.texto_legal_pie}
                onChange={(e) => setDocumento((prev) => ({ ...prev, texto_legal_pie: e.target.value }))}
              />
            </Field>

            <div className="flex justify-end">
              <Button
                data-testid="settings-save-documento"
                onClick={handleSaveDocumento}
                disabled={savingDocumento}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {savingDocumento ? "Guardando..." : "Guardar documento"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ===== CATEGORÍAS DE PROVEEDOR (roadmap 3.6) ===== */}
        <TabsContent value="categorias" className="mt-4">
          <SupplierCategoriesSettings />
        </TabsContent>

        {/* ===== ALERTAS (destinatarios email) ===== */}
        {/* ---- Pestaña IA (A5/§7: consentimiento explícito, off por defecto) ---- */}
        <TabsContent value="ia" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5 max-w-2xl" data-testid="settings-bloque-ia">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Funciones con inteligencia artificial</h3>
              <p className="text-sm text-slate-500">
                La lectura automática de facturas y nóminas (OCR) y el análisis del CFO usan
                la API de Google Gemini.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-sm text-slate-600 space-y-2">
              <p>
                Si las activas, el <strong>texto de los documentos que subas al OCR</strong>,
                la <strong>imagen del documento</strong> cuando subes una foto, una imagen o un
                PDF escaneado sin texto, y <strong>resúmenes de tus cifras</strong> (para el
                análisis financiero) se envían a Google para generar el resultado.
                Nada más sale de la aplicación.
              </p>
              <p>
                Con las funciones desactivadas, el OCR usa un lector local más
                limitado que <strong>solo funciona con PDFs que contienen texto</strong>:
                las imágenes y los PDFs escaneados no se pueden procesar, y el
                análisis del CFO no se genera. Puedes cambiarlo cuando quieras;
                más detalle en la{" "}
                <a href="/privacidad" target="_blank" rel="noopener" className="text-blue-600 underline">
                  política de privacidad
                </a>.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={iaConsent}
                onCheckedChange={setIaConsent}
                data-testid="settings-ia-consent-switch"
              />
              <label className="text-sm text-slate-700">
                Doy mi consentimiento para usar las funciones de IA con mis datos
              </label>
            </div>
            <Button
              onClick={handleSaveIa}
              disabled={savingIa}
              data-testid="settings-save-ia-btn"
            >
              {savingIa ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="alertas" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 max-w-3xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Destinatarios de Email</h3>
                <p className="text-sm text-slate-500">Direcciones que reciben las alertas por email</p>
              </div>
              <Button
                size="sm"
                data-testid="settings-add-recipient-btn"
                onClick={() => {
                  setEditingRecipient(null);
                  setRecipientForm({ name: "", email: "", is_active: true });
                  setShowRecipientForm(true);
                }}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Plus className="w-4 h-4 mr-2" /> Añadir
              </Button>
            </div>

            {recipients.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>No hay destinatarios configurados</p>
                <p className="text-sm">Añade direcciones de email para recibir alertas</p>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-xs text-slate-500 uppercase">
                      <th className="px-4 py-2.5 text-left font-semibold">Nombre</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Email</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Activo</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map(r => (
                      <tr key={r.recipient_id} className="border-b border-slate-100 hover:bg-slate-50/80" data-testid={`settings-recipient-${r.recipient_id}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-900">{r.name}</td>
                        <td className="px-4 py-2.5 text-slate-600">{r.email}</td>
                        <td className="px-4 py-2.5 text-center">
                          <Switch checked={r.is_active} onCheckedChange={() => handleToggleRecipient(r)} />
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => {
                              setEditingRecipient(r);
                              setRecipientForm({ name: r.name, email: r.email, is_active: r.is_active });
                              setShowRecipientForm(true);
                            }} className="p-1.5 rounded hover:bg-slate-100">
                              <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                            </button>
                            <button onClick={() => handleDeleteRecipient(r.recipient_id)} className="p-1.5 rounded hover:bg-red-50">
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== IMPORTAR DATOS (roadmap 2.4) ===== */}
        <TabsContent value="importar" className="mt-4">
          <DataImport />
        </TabsContent>
      </Tabs>

      {/* Recipient Form Dialog */}
      <Dialog open={showRecipientForm} onOpenChange={setShowRecipientForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRecipient ? "Editar Destinatario" : "Nuevo Destinatario"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Nombre</label>
              <Input
                data-testid="settings-recipient-name-input"
                value={recipientForm.name}
                onChange={(e) => setRecipientForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nombre del destinatario"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Email</label>
              <Input
                type="email"
                data-testid="settings-recipient-email-input"
                value={recipientForm.email}
                onChange={(e) => setRecipientForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@ejemplo.com"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={recipientForm.is_active}
                onCheckedChange={(val) => setRecipientForm(prev => ({ ...prev, is_active: val }))}
              />
              <label className="text-sm">Activo</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecipientForm(false)}>Cancelar</Button>
            <Button
              data-testid="settings-save-recipient-btn"
              onClick={handleSaveRecipient}
              disabled={!recipientForm.name || !recipientForm.email}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {editingRecipient ? "Actualizar" : "Añadir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
